import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useSnackbar } from 'notistack';
import { EQueryUserCRUD } from './userCRUD';
import {
  APIGetUsersWithBranches,
  APIGetAllBranches,
  APIGetAllBranchesNoPagination,
  APIGetDashboardStats,
  APIGetUserBranches,
  APIAssignBranchToUser,
  APIRemoveBranchFromUser,
  APIRemoveAllBranchesFromUser,
  APIGetMigrationStatus,
  APIMigrateExistingData,
  APISetDefaultBranch,
  // Admin User Management APIs
  APIAdminGetBranchDetail,
  APIAdminGetUsersList,
  APIAdminAddUserToBranch,
  APIAdminSetPrimaryUser,
  APIAdminRemoveUserFromBranch,
  APIAdminUpdateUserPhone,
  APIAdminDemoteToSecondary,
  APIAdminGetUserCustomerCompanies,
  APIAdminSetUserCustomerCompanies,
} from '../../service/api/APIUserBranch';
import {
  IUserWithBranches,
  IUserWithBranchesAPIResponse,
  IBranch,
  IBranchAPIResponse,
  IDashboardStats,
  IDashboardStatsResponse,
  IAssignBranchRequest,
  IRemoveBranchRequest,
  IRemoveAllBranchesRequest,
  EUserBranchQueryKeys,
  // User Branches Response Interfaces
  IUserBranchesResponse,
  ISetDefaultBranchRequest,
  // Admin User Management Interfaces
  IAdminBranchDetailResponse,
  IAdminUsersQueryParams,
  IAdminUsersListResponse,
  IAdminAddUserToBranchRequest,
  IAdminSetPrimaryUserRequest,
  IAdminUpdateUserPhoneRequest,
  IAdminDemoteToSecondaryRequest,
  IAdminSetUserCustomerCompaniesRequest,
  IAdminUserCustomerCompaniesResponse,
  EAdminUserManagementQueryKeys,
} from '../../compiler/userBranch.interface';

// Get Users with Branches
export const useGetUsersWithBranches = (params?: any) => {
  return useQuery<IUserWithBranchesAPIResponse>(
    [EUserBranchQueryKeys.GET_USERS_WITH_BRANCHES, params],
    () => APIGetUsersWithBranches(params),
    {
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    }
  );
};

// Get All Branches with pagination
export const useGetAllBranches = (params?: {
  page?: number;
  limit?: number;
  search?: string;
  franchise_uuid?: string;
  province?: string;
  district?: string;
}) => {
  return useQuery<{
    data: IBranch[];
    meta: {
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    };
  }>(
    [EUserBranchQueryKeys.GET_ALL_BRANCHES, params],
    () => APIGetAllBranches(params),
    {
      retry: 1,
      staleTime: 10 * 60 * 1000, // 10 minutes
      keepPreviousData: true, // Keep previous data while loading new page
    }
  );
};

// Get All Branches without pagination (for selection modals)
export const useGetAllBranchesNoPagination = (params?: {
  search?: string;
  franchise_uuid?: string;
  province?: string;
  district?: string;
}) => {
  return useQuery<IBranch[]>(
    [EUserBranchQueryKeys.GET_ALL_BRANCHES, 'no-pagination', params],
    () => APIGetAllBranchesNoPagination(params),
    {
      retry: 1,
      staleTime: 30 * 60 * 1000, // 30 minutes - longer cache for non-paginated data
      cacheTime: 60 * 60 * 1000, // 1 hour cache time
    }
  );
};

// Get Dashboard Stats
export const useGetDashboardStats = () => {
  return useQuery<IDashboardStatsResponse>(
    [EUserBranchQueryKeys.GET_DASHBOARD_STATS],
    APIGetDashboardStats,
    {
      retry: 1,
      refetchInterval: 30 * 1000, // Refresh every 30 seconds
    }
  );
};

// Get User Branches by User UUID
export const useGetUserBranches = (userUuid: string, enabled: boolean = true) => {
  return useQuery<IUserBranchesResponse>(
    [EUserBranchQueryKeys.GET_USER_BRANCHES, userUuid],
    () => APIGetUserBranches(userUuid),
    {
      enabled: enabled && !!userUuid,
      retry: 1,
    }
  );
};

// Get Migration Status
export const useGetMigrationStatus = () => {
  return useQuery(
    [EUserBranchQueryKeys.GET_MIGRATION_STATUS],
    APIGetMigrationStatus,
    {
      retry: 1,
    }
  );
};

// Assign Branch to User Mutation
export const useAssignBranchToUser = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation(APIAssignBranchToUser, {
    onSuccess: (data, variables) => {
      if (data.success) {
        enqueueSnackbar('เพิ่มสาขาสำเร็จ', { variant: 'success' });
        
        // Invalidate related queries
        queryClient.invalidateQueries([EUserBranchQueryKeys.GET_USERS_WITH_BRANCHES]);
        queryClient.invalidateQueries([EUserBranchQueryKeys.GET_USER_BRANCHES, variables.user_uuid]);
        queryClient.invalidateQueries([EUserBranchQueryKeys.GET_DASHBOARD_STATS]);
      } else {
        enqueueSnackbar(data.message || 'เกิดข้อผิดพลาดในการเพิ่มสาขา', { 
          variant: 'error' 
        });
      }
    },
    onError: (error: any) => {
      console.error('Error assigning branch:', error);
      enqueueSnackbar('เกิดข้อผิดพลาดในการเชื่อมต่อ', { variant: 'error' });
    },
  });
};

// Remove Branch from User Mutation
export const useRemoveBranchFromUser = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation(APIRemoveBranchFromUser, {
    onSuccess: (data, variables) => {
      if (data.success) {
        enqueueSnackbar('ลบสาขาสำเร็จ', { variant: 'success' });
        
        // Invalidate related queries
        queryClient.invalidateQueries([EUserBranchQueryKeys.GET_USERS_WITH_BRANCHES]);
        queryClient.invalidateQueries([EUserBranchQueryKeys.GET_USER_BRANCHES, variables.user_uuid]);
        queryClient.invalidateQueries([EUserBranchQueryKeys.GET_DASHBOARD_STATS]);
      } else {
        enqueueSnackbar(data.message || 'เกิดข้อผิดพลาดในการลบสาขา', { 
          variant: 'error' 
        });
      }
    },
    onError: (error: any) => {
      console.error('Error removing branch:', error);
      enqueueSnackbar('เกิดข้อผิดพลาดในการเชื่อมต่อ', { variant: 'error' });
    },
  });
};

// Migrate Existing Data Mutation
export const useMigrateExistingData = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation(APIMigrateExistingData, {
    onSuccess: (data) => {
      enqueueSnackbar('Migration สำเร็จ', { variant: 'success' });
      
      // Invalidate all related queries after migration
      queryClient.invalidateQueries([EUserBranchQueryKeys.GET_USERS_WITH_BRANCHES]);
      queryClient.invalidateQueries([EUserBranchQueryKeys.GET_DASHBOARD_STATS]);
      queryClient.invalidateQueries([EUserBranchQueryKeys.GET_MIGRATION_STATUS]);
    },
    onError: (error: any) => {
      console.error('Error during migration:', error);
      enqueueSnackbar('เกิดข้อผิดพลาดระหว่าง Migration', { variant: 'error' });
    },
  });
};

// Remove All Branches from User Mutation
export const useRemoveAllBranchesFromUser = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation(APIRemoveAllBranchesFromUser, {
    onSuccess: (data, variables) => {
      if (data.success) {
        enqueueSnackbar('ลบสาขาทั้งหมดสำเร็จ', { variant: 'success' });
        
        // Invalidate related queries
        queryClient.invalidateQueries([EUserBranchQueryKeys.GET_USERS_WITH_BRANCHES]);
        queryClient.invalidateQueries([EUserBranchQueryKeys.GET_USER_BRANCHES, variables.user_uuid]);
        queryClient.invalidateQueries([EUserBranchQueryKeys.GET_DASHBOARD_STATS]);
      } else {
        enqueueSnackbar(data.message || 'เกิดข้อผิดพลาดในการลบสาขา', { 
          variant: 'error' 
        });
      }
    },
    onError: (error: any) => {
      console.error('Error removing all branches:', error);
      enqueueSnackbar('เกิดข้อผิดพลาดในการเชื่อมต่อ', { variant: 'error' });
    },
  });
};

// Set Default Branch for User Mutation
export const useSetDefaultBranch = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation(APISetDefaultBranch, {
    onSuccess: (data, variables) => {
      if (data.success) {
        enqueueSnackbar('ตั้งค่าสาขา Default สำเร็จ', { variant: 'success' });
        
        // Invalidate related queries
        queryClient.invalidateQueries([EUserBranchQueryKeys.GET_USER_BRANCHES, variables.user_uuid]);
        queryClient.invalidateQueries([EUserBranchQueryKeys.GET_USERS_WITH_BRANCHES]);
      } else {
        enqueueSnackbar(data.message || 'เกิดข้อผิดพลาดในการตั้งค่าสาขา Default', { 
          variant: 'error' 
        });
      }
    },
    onError: (error: any) => {
      console.error('Error setting default branch:', error);
      const message = error?.response?.data?.message || 'เกิดข้อผิดพลาดในการเชื่อมต่อ';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
};

// =====================================================
// Admin User Management Hooks (Multi-User Branch System)
// =====================================================

/**
 * Get Branch Detail with Users (Admin)
 * Used in LineRegistrationTab to display all LINE users for a branch
 */
export const useAdminGetBranchDetail = (branchUuid: string | undefined, enabled: boolean = true) => {
  return useQuery<IAdminBranchDetailResponse>(
    [EAdminUserManagementQueryKeys.GET_BRANCH_DETAIL, branchUuid],
    () => APIAdminGetBranchDetail(branchUuid!),
    {
      enabled: enabled && !!branchUuid,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    }
  );
};

/**
 * Get Users List (Admin)
 * Used for user search when adding users to branches
 */
export const useAdminGetUsersList = (params?: IAdminUsersQueryParams, enabled: boolean = true) => {
  return useQuery<IAdminUsersListResponse>(
    [EAdminUserManagementQueryKeys.GET_USERS_LIST, params],
    () => APIAdminGetUsersList(params),
    {
      enabled,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
      keepPreviousData: true,
    }
  );
};

/**
 * Get Customer Company Scope for User (Admin)
 * Used to restrict customer-facing billing visibility by company.
 */
export const useAdminGetUserCustomerCompanies = (userUuid: string | undefined, enabled: boolean = true) => {
  return useQuery<IAdminUserCustomerCompaniesResponse>(
    [EAdminUserManagementQueryKeys.GET_USER_CUSTOMER_COMPANIES, userUuid],
    () => APIAdminGetUserCustomerCompanies(userUuid!),
    {
      enabled: enabled && !!userUuid,
      retry: 1,
      staleTime: 5 * 60 * 1000,
    }
  );
};

/**
 * Set Customer Company Scope for User (Admin)
 * Keeps menu permission separate from customer data visibility.
 */
export const useAdminSetUserCustomerCompanies = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation(
    (data: IAdminSetUserCustomerCompaniesRequest & { user_uuid: string }) =>
      APIAdminSetUserCustomerCompanies(data),
    {
      onSuccess: (data, variables) => {
        enqueueSnackbar('บันทึกขอบเขตลูกค้าสำเร็จ', { variant: 'success' });
        queryClient.invalidateQueries([
          EAdminUserManagementQueryKeys.GET_USER_CUSTOMER_COMPANIES,
          variables.user_uuid,
        ]);
        queryClient.invalidateQueries([EAdminUserManagementQueryKeys.GET_USERS_LIST]);
        queryClient.invalidateQueries(EQueryUserCRUD.GetAllUsers);
      },
      onError: (error: any) => {
        console.error('Error setting customer company scope:', error);
        const message = error?.response?.data?.message || 'เกิดข้อผิดพลาดในการบันทึกขอบเขตลูกค้า';
        enqueueSnackbar(message, { variant: 'error' });
      },
    }
  );
};

/**
 * Add User to Branch (Admin)
 * Allows admin to directly add a user to a branch
 */
export const useAdminAddUserToBranch = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation(APIAdminAddUserToBranch, {
    onSuccess: (data, variables) => {
      if (data.success) {
        enqueueSnackbar(data.message || 'เพิ่ม User เข้าสาขาสำเร็จ', { variant: 'success' });
        
        // Invalidate branch detail query
        queryClient.invalidateQueries([EAdminUserManagementQueryKeys.GET_BRANCH_DETAIL, variables.branch_uuid]);
        queryClient.invalidateQueries([EAdminUserManagementQueryKeys.GET_USERS_LIST]);
        queryClient.invalidateQueries(['branch-line-users', variables.branch_uuid]);
      } else {
        enqueueSnackbar(data.message || 'เกิดข้อผิดพลาดในการเพิ่ม User', { 
          variant: 'error' 
        });
      }
    },
    onError: (error: any) => {
      console.error('Error adding user to branch:', error);
      const message = error?.response?.data?.message || 'เกิดข้อผิดพลาดในการเชื่อมต่อ';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
};

/**
 * Set Primary User (Admin)
 * Sets a user as PRIMARY for a branch (admin override)
 */
export const useAdminSetPrimaryUser = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation(APIAdminSetPrimaryUser, {
    onSuccess: (data, variables) => {
      if (data.success) {
        enqueueSnackbar(data.message || 'ตั้ง User หลักสำเร็จ', { variant: 'success' });
        
        // Invalidate branch detail query
        queryClient.invalidateQueries([EAdminUserManagementQueryKeys.GET_BRANCH_DETAIL, variables.branch_uuid]);
        queryClient.invalidateQueries(['branch-line-users', variables.branch_uuid]);
      } else {
        enqueueSnackbar(data.message || 'เกิดข้อผิดพลาดในการตั้ง User หลัก', { 
          variant: 'error' 
        });
      }
    },
    onError: (error: any) => {
      console.error('Error setting primary user:', error);
      const message = error?.response?.data?.message || 'เกิดข้อผิดพลาดในการเชื่อมต่อ';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
};

/**
 * Remove User from Branch (Admin)
 * Removes user's access to a specific branch
 */
export const useAdminRemoveUserFromBranch = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation(
    ({ userUuid, branchUuid }: { userUuid: string; branchUuid: string }) => 
      APIAdminRemoveUserFromBranch(userUuid, branchUuid),
    {
      onSuccess: (data, variables) => {
        if (data.success) {
          enqueueSnackbar(data.message || 'ลบ User ออกจากสาขาสำเร็จ', { variant: 'success' });
          
          // Invalidate branch detail query
          queryClient.invalidateQueries([EAdminUserManagementQueryKeys.GET_BRANCH_DETAIL, variables.branchUuid]);
          queryClient.invalidateQueries([EAdminUserManagementQueryKeys.GET_USERS_LIST]);
          queryClient.invalidateQueries(['branch-line-users', variables.branchUuid]);
        } else {
          enqueueSnackbar(data.message || 'เกิดข้อผิดพลาดในการลบ User', { 
            variant: 'error' 
          });
        }
      },
      onError: (error: any) => {
        console.error('Error removing user from branch:', error);
        const message = error?.response?.data?.message || 'เกิดข้อผิดพลาดในการเชื่อมต่อ';
        enqueueSnackbar(message, { variant: 'error' });
      },
    }
  );
};

/**
 * Update User Phone (Admin)
 * Admin can update phone and/or verified_phone of a user directly (no OTP required)
 */
export const useAdminUpdateUserPhone = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation(
    (data: IAdminUpdateUserPhoneRequest) => APIAdminUpdateUserPhone(data),
    {
      onSuccess: (data) => {
        if (data.success) {
          enqueueSnackbar(data.message || 'แก้ไขเบอร์โทรสำเร็จ', { variant: 'success' });
          queryClient.invalidateQueries([EAdminUserManagementQueryKeys.GET_BRANCH_DETAIL]);
          queryClient.invalidateQueries(['branch-line-users']);
          queryClient.invalidateQueries(['customer-branch']);
          queryClient.invalidateQueries([EUserBranchQueryKeys.GET_USERS_WITH_BRANCHES]);
        } else {
          enqueueSnackbar(data.message || 'เกิดข้อผิดพลาดในการแก้ไขเบอร์', { variant: 'error' });
        }
      },
      onError: (error: any) => {
        console.error('Error updating user phone:', error);
        const message = error?.response?.data?.message || 'เกิดข้อผิดพลาดในการเชื่อมต่อ';
        enqueueSnackbar(message, { variant: 'error' });
      },
    }
  );
};

/**
 * Demote User to Secondary (Admin)
 * Demotes a PRIMARY user to SECONDARY, leaving the branch ready for re-registration
 */
export const useAdminDemoteToSecondary = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation(
    (data: IAdminDemoteToSecondaryRequest) => APIAdminDemoteToSecondary(data),
    {
      onSuccess: (data) => {
        if (data.success) {
          enqueueSnackbar(data.message || 'ปลด User เป็น Secondary สำเร็จ', { variant: 'success' });
          queryClient.invalidateQueries([EAdminUserManagementQueryKeys.GET_BRANCH_DETAIL]);
          queryClient.invalidateQueries(['branch-line-users']);
          queryClient.invalidateQueries(['customer-branch']);
        } else {
          enqueueSnackbar(data.message || 'เกิดข้อผิดพลาด', { variant: 'error' });
        }
      },
      onError: (error: any) => {
        console.error('Error demoting user:', error);
        const message = error?.response?.data?.message || 'เกิดข้อผิดพลาดในการเชื่อมต่อ';
        enqueueSnackbar(message, { variant: 'error' });
      },
    }
  );
};
